import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Tab, Box, Button, Grid, Tooltip } from '@mui/material';
import ProductsTable from './ProductsTable';
import { useAppDispatch, useAppSelector } from '../../Shared/Types/rtk';
import { billingsAction } from '../../Shared/Store/Slice/ProductList';
import { ThemeProvider } from '@emotion/react';
import { tabs } from '../../Common/Theme';
import { AddOutlined, CloseOutlined, TvOutlined, TvOffOutlined } from '@mui/icons-material';

const BILLING_CHANNEL = 'billing-display';
const DISPLAY_PREF_KEY = 'customerDisplayEnabled';

const BillingsTabs = () => {
    const billingsTabs = useAppSelector((state) => state.billingsTabs);
    const dispatch = useAppDispatch();

    const [tabIndex, setTabIndex] = useState(0);
    const [displayEnabled, setDisplayEnabled] = useState(
        () => localStorage.getItem(DISPLAY_PREF_KEY) === 'true'
    );
    const channelRef = useRef<BroadcastChannel | null>(null);
    const displayWindowRef = useRef<Window | null>(null);

    useEffect(() => {
        channelRef.current = new BroadcastChannel(BILLING_CHANNEL);
        return () => channelRef.current?.close();
    }, []);

    // Auto-open the display window when billing loads if it was previously enabled
    useEffect(() => {
        if (displayEnabled) {
            displayWindowRef.current = window.open('/display', 'CustomerDisplay', 'width=1280,height=800,toolbar=0,menubar=0,location=0');
        }
    }, []);

    const activeBilling = billingsTabs[tabIndex]?.billingDetail;
    useEffect(() => {
        if (activeBilling && channelRef.current) {
            channelRef.current.postMessage(activeBilling);
        }
    }, [activeBilling]);

    const toggleCustomerDisplay = () => {
        if (displayEnabled) {
            // Turn off: close the window if we have a reference, save preference
            displayWindowRef.current?.close();
            displayWindowRef.current = null;
            localStorage.setItem(DISPLAY_PREF_KEY, 'false');
            setDisplayEnabled(false);
        } else {
            // Turn on: open the window, save preference
            displayWindowRef.current = window.open('/display', 'CustomerDisplay', 'width=1280,height=800,toolbar=0,menubar=0,location=0');
            localStorage.setItem(DISPLAY_PREF_KEY, 'true');
            setDisplayEnabled(true);
        }
    };
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const addNewTab = () => {
        dispatch(billingsAction.addBillingTab())
        setTabIndex(billingsTabs.length);
    };

    const removeTab = (index: number) => {
        dispatch(billingsAction.removeBillingTab({ index }))
        if (index === tabIndex) {
            if (tabIndex > 0) {
                setTabIndex(tabIndex - 1);
            } else {
                setTabIndex(0);
            }
        } else if (index < tabIndex) {
            setTabIndex(tabIndex - 1);
        }
    };

    return (
        <div>
            <Box sx={{ borderBottom: 1, borderColor: '#efefef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <ThemeProvider theme={tabs}>
                    <Tabs value={tabIndex} onChange={handleChange} aria-label="tabs">
                        {billingsTabs.map((tab, index) => (
                            <Tab
                                key={tab.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        Billing {index + 1}
                                        <CloseOutlined onClick={() => removeTab(index)} sx={{ fontSize: 16, marginLeft: '15px' }} />
                                    </Box>
                                }
                            />
                        ))}
                        <Button onClick={addNewTab} variant='secondary'>
                            <AddOutlined sx={{ fontSize: 16 }} />
                        </Button>
                    </Tabs>
                </ThemeProvider>
                <Tooltip title={displayEnabled ? 'Disable Customer Display' : 'Enable Customer Display'}>
                    <Button onClick={toggleCustomerDisplay} variant='secondary' sx={{ mr: 1, minWidth: 'unset', color: displayEnabled ? 'success.main' : 'inherit' }}>
                        {displayEnabled ? <TvOutlined sx={{ fontSize: 20 }} /> : <TvOffOutlined sx={{ fontSize: 20 }} />}
                    </Button>
                </Tooltip>
            </Box>
            <div>
                {billingsTabs.map((billing, index) => (
                    <div
                        key={billing.id}
                        role="tabpanel"
                        hidden={tabIndex !== index}
                        id={`tabpanel-${billing.id}`}
                        aria-labelledby={`tab-${billing.id}`}
                    >
                        {tabIndex === index &&
                            <Grid container spacing={2} alignItems='start' justifyContent='start' alignContent='baseline' flexDirection='row-reverse'>
                                <ProductsTable tableIndex={tabIndex} billing={billing} />
                            </Grid>
                        }
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BillingsTabs;
